// Open Trade Packet generation (ADR_LOS-026/015): fetches the packet payload,
// builds the two doc-group zips, assembles the .eml, and downloads it. Shared
// by the Actions-menu prepare and the stale-icon Re-send popup so both produce
// the identical email.
import { createOpenTradePacket } from 'app/state/ducks/signable-document/thunks'
import {
  buildZip,
  dedupeEntryNames,
  OPEN_TRADE_PACKET_PATH_MAX,
} from 'app/utils/zip-builder'
import { downloadFromUrl } from 'app/utils/util-functions'
import {
  arrayBufferToBase64,
  DEFAULT_COMPANY_NAME,
  DEFAULT_OPERATIONS_EMAIL,
} from './trade-eml-builder'
import {
  buildOpenTradeEml,
  OpenTradeEmlAttachment,
} from './open-trade-eml-builder'

export type OpenTradePacketPayload = {
  zipBaseName: string
  zips: { group: string; name: string }[]
  documents: { group: string; fileName: string; url: string }[]
  missing: {
    subTradeId: string | null
    buyerName: string | null
    docType: string
  }[]
  email: {
    agentEntityName: string
    toEmails: string[]
    masterTradeSystemId: string
    dealName: string
    dealCode: string
    tradeDate: string
    facilityNames: string[]
    tcAmount: string
    aaAmount: string
    sellers: string[]
    buyers: string[]
    selectedCount: number
    totalCount: number
    attachedDocGroups: string[]
  }
}

export type OpenTradePacketGenerationResult =
  | { ok: true }
  // `blocked` distinguishes a validation refusal (an AA that is not fully
  // executed, LOS-6934) from a generation failure. A block must leave no trace:
  // no Prepared record, no icon change, no per-sub-trade audit row.
  | { ok: false; message: string; blocked: boolean }

export const generateOpenTradePacketEml = async (
  subTradeIds: string[],
): Promise<OpenTradePacketGenerationResult> => {
  const response: any = await createOpenTradePacket(subTradeIds)
  if (!response.success) {
    return {
      ok: false,
      message: String(response.payload),
      blocked: response.status === 422,
    }
  }
  const packet: OpenTradePacketPayload = response.payload

  // One zip per doc group that has documents. Entries live under a top-level
  // folder named like the zip, so extraction always yields a folder even when
  // the zip holds a single file — and because Windows "Extract All" also
  // creates a destination folder from the zip name, that folder name is spent
  // twice in the extracted path. Names come from the backend already capped;
  // the assert below re-checks the paths this function actually writes.
  const attachments: OpenTradeEmlAttachment[] = []
  for (const { group, name: zipName } of packet.zips) {
    const docs = packet.documents.filter(d => d.group === group)
    if (!docs.length) continue
    const entries = await Promise.all(
      docs.map(async d => {
        const res = await fetch(d.url)
        if (!res.ok) throw new Error(`Failed to fetch ${d.fileName}`)
        return {
          name: d.fileName,
          data: new Uint8Array(await res.arrayBuffer()),
        }
      }),
    )
    const named = dedupeEntryNames(entries).map(e => ({
      ...e,
      name: `${zipName}/${e.name}`,
    }))
    const tooLong = named.find(e => e.name.length > OPEN_TRADE_PACKET_PATH_MAX)
    if (tooLong) {
      return {
        ok: false,
        message: `Open Trade Packet path budget exceeded: "${tooLong.name}" is ${tooLong.name.length} characters (max ${OPEN_TRADE_PACKET_PATH_MAX}). The packet was not generated.`,
        blocked: false,
      }
    }
    attachments.push({
      fileName: `${zipName}.zip`,
      base64: arrayBufferToBase64(buildZip(named).buffer as ArrayBuffer),
      mimeType: 'application/zip',
    })
  }

  const eml = buildOpenTradeEml({
    ...packet.email,
    companyName: DEFAULT_COMPANY_NAME,
    to: packet.email.toEmails,
    operationsEmail: DEFAULT_OPERATIONS_EMAIL,
    attachments,
  })
  const blob = new Blob([eml], { type: 'message/rfc822' })
  const url = URL.createObjectURL(blob)
  downloadFromUrl(url, `${packet.zipBaseName}_Open_Trade_Packet.eml`)
  setTimeout(() => URL.revokeObjectURL(url), 30000)

  return { ok: true }
}
